import { useRef } from 'react';

/**
 * useSwipe — minimal touch/pointer swipe + press-hold detector for
 * hero & promo carousels.
 *
 * Returns event handlers you spread onto the carousel container:
 *   <div {...useSwipe({ onSwipeLeft, onSwipeRight, onPressStart, onPressEnd })} />
 *
 * Behavior:
 * - Tracks the first touch / pointer-down position and time.
 * - Fires `onPressStart` immediately on pointerdown / touchstart so the
 *   parent can pause its auto-rotation. Pair it with `onPressEnd` to
 *   resume.
 * - Fires `onSwipeLeft` / `onSwipeRight` on release IF:
 *     · horizontal distance ≥ minDistance (default 50px), AND
 *     · |dx| > |dy| (vertical drags pass through to page scroll).
 * - Does NOT call `preventDefault` on touchmove — vertical scroll still
 *   works exactly as before.
 * - `onPressEnd` fires on EVERY release, regardless of swipe outcome,
 *   so the carousel can resume cleanly whether the user swiped or just
 *   tapped.
 *
 * No state, no re-renders. Designed to be cheap on mobile.
 */
const useSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  onPressStart,
  onPressEnd,
  minDistance = 50,
} = {}) => {
  const startRef = useRef(null);

  const begin = (x, y) => {
    startRef.current = { x, y, t: Date.now() };
    if (onPressStart) onPressStart();
  };

  const end = (x, y) => {
    const start = startRef.current;
    startRef.current = null;
    if (onPressEnd) onPressEnd();
    if (!start) return;
    const dx = x - start.x;
    const dy = y - start.y;
    if (Math.abs(dx) < minDistance) return;
    if (Math.abs(dx) <= Math.abs(dy)) return; // mostly vertical → ignore
    if (dx < 0 && onSwipeLeft) onSwipeLeft();
    else if (dx > 0 && onSwipeRight) onSwipeRight();
  };

  const cancel = () => {
    if (startRef.current && onPressEnd) onPressEnd();
    startRef.current = null;
  };

  return {
    onTouchStart: (e) => {
      const t = e.touches?.[0];
      if (t) begin(t.clientX, t.clientY);
    },
    onTouchEnd: (e) => {
      const t = e.changedTouches?.[0];
      if (t) end(t.clientX, t.clientY);
    },
    onTouchCancel: cancel,
    onPointerDown: (e) => {
      // Only react to primary mouse button / first pointer
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      begin(e.clientX, e.clientY);
    },
    onPointerUp: (e) => {
      end(e.clientX, e.clientY);
    },
    onPointerCancel: cancel,
    // Leaving the element while still pressing also counts as a release
    // (otherwise auto-rotation could stay paused after the finger slides
    // off the card without lifting). We do NOT evaluate swipe-distance
    // on leave — the user might just be scrolling past — so resume the
    // carousel without firing a phantom swipe.
    onPointerLeave: () => {
      if (startRef.current) {
        startRef.current = null;
        if (onPressEnd) onPressEnd();
      }
    },
  };
};

export default useSwipe;
